import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { config } from '../../config/appConfig';
import { useLanguage } from '../../contexts/LanguageContext';
import useWebSocket from '../../hooks/useWebSocket';
import DoodleJumpGame from './DoodleJumpGame';
import Terraria2Game from './Terraria2Game';
import './GameContainer.css';

const GameContainer = ({ isActive, onClose, accentColor = 'purple', defaultGame = 'terraria', className = '' }) => {
    const { language } = useLanguage();
    const isEn = language === 'en';
    const [selectedGame, setSelectedGame] = useState(defaultGame);
    const [isExpanded, setIsExpanded] = useState(false);
    const [hasBeenOpened, setHasBeenOpened] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Mini announcement banner for the new GTA release
    const [showGtaInfo, setShowGtaInfo] = useState(() => {
        try {
            return !sessionStorage.getItem('gta_promo_dismissed');
        } catch {
            return true;
        }
    });

    const handleDismissGtaInfo = useCallback((e) => {
        if (e) e.stopPropagation();
        setShowGtaInfo(false);
        try {
            sessionStorage.setItem('gta_promo_dismissed', '1');
        } catch {}
    }, []);

    // Detect touch/mobile device once — used for height differences
    const [isTouchDevice] = useState(() => ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (typeof window !== 'undefined' && window.innerWidth <= 768));
    const terrariaHeight = isTouchDevice ? '380px' : '560px';
    const doodleHeight = isTouchDevice ? '360px' : '420px';
    const gtaHeight = isTouchDevice ? '400px' : '580px';
    // Track which games have been initialized — once true, stays true forever
    const [initDoodle, setInitDoodle] = useState(false);
    const [initTerraria, setInitTerraria] = useState(false);
    const [initGta, setInitGta] = useState(false);
    const [gtaLoaded, setGtaLoaded] = useState(false);
    const [gtaLoadStep, setGtaLoadStep] = useState(0);
    const resizeTimerRef = useRef(null);
    const containerRef = useRef(null);
    const gtaIframeRef = useRef(null);

    // Auth and WebSocket Leaderboard state
    const { isAuthenticated, token, user } = useAuth();
    const { isConnected: isWsConnected, subscribe, sendMessage } = useWebSocket();
    const [gtaLeaderboard, setGtaLeaderboard] = useState([]);
    const [isLoadingGtaTop, setIsLoadingGtaTop] = useState(false);
    const [gtaPlayTime, setGtaPlayTime] = useState(() => {
        try {
            return parseInt(localStorage.getItem('gta_play_time') || '0', 10) || 0;
        } catch {
            return 0;
        }
    });
    const saveGtaScoreRef = useRef(null);
    const lastHttpSaveRef = useRef(0);

    const formatPlayTime = useCallback((totalSeconds) => {
        const sec = Math.floor(totalSeconds || 0);
        const hrs = Math.floor(sec / 3600);
        const mins = Math.floor((sec % 3600) / 60);
        const s = sec % 60;
        if (hrs > 0) {
            return isEn ? `${hrs}h ${mins}m` : `${hrs}ч ${mins}м`;
        }
        if (mins > 0) {
            return isEn ? `${mins}m ${s}s` : `${mins}м ${s}с`;
        }
        return isEn ? `${s}s` : `${s}с`;
    }, [isEn]);

    const [backendConnected, setBackendConnected] = useState(false);

    const fetchGtaLeaderboard = useCallback(async () => {
        setIsLoadingGtaTop(true);
        try {
            const response = await axios.get(`${config.api.baseUrl}/api/public/leaderboard/gta`);
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                setGtaLeaderboard(response.data.data);
                setBackendConnected(true);
            } else {
                setBackendConnected(false);
            }
        } catch (error) {
            console.warn('[GTA Leaderboard] Fetch failed:', error.message);
            setBackendConnected(false);
        } finally {
            setIsLoadingGtaTop(false);
        }
    }, []);

    // Recover personal best playtime from backend if higher than local
    useEffect(() => {
        if (!isAuthenticated || !token) return;
        let isMounted = true;
        (async () => {
            try {
                const res = await axios.get(`${config.api.baseUrl}/api/account/score/gta`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (isMounted && res.data?.success && typeof res.data.data === 'number' && res.data.data > 0) {
                    const serverScore = res.data.data;
                    setGtaPlayTime(prev => {
                        const best = Math.max(prev, serverScore);
                        try { localStorage.setItem('gta_play_time', String(best)); } catch {}
                        return best;
                    });
                }
            } catch (e) {}
        })();
        return () => { isMounted = false; };
    }, [isAuthenticated, token]);

    // WebSocket real-time subscription for instant leaderboard synchronization across all users
    useEffect(() => {
        if (!isWsConnected) return;

        const handleLeaderboardMsg = (data) => {
            const list = Array.isArray(data)
                ? data
                : (data?.data && Array.isArray(data.data))
                    ? data.data
                    : (data?.entries && Array.isArray(data.entries))
                        ? data.entries
                        : null;
            if (list) {
                setGtaLeaderboard(list);
            }
        };

        const unsubSpecific = subscribe(`/topic/leaderboard/gta`, handleLeaderboardMsg);
        const unsubGeneral = subscribe(`/topic/leaderboard`, (msg) => {
            if (msg?.gameId === 'gta' && Array.isArray(msg.entries)) {
                setGtaLeaderboard(msg.entries);
            }
        });

        return () => {
            if (unsubSpecific) unsubSpecific();
            if (unsubGeneral) unsubGeneral();
        };
    }, [isWsConnected, subscribe]);

    const saveGtaScore = useCallback(async (timeInSeconds) => {
        if (!isAuthenticated || !token || timeInSeconds <= 0) return;

        // 1. Instant WebSocket broadcast trigger if connected
        if (isWsConnected) {
            try {
                sendMessage('/score/gta', { score: timeInSeconds });
            } catch (e) {}
        }

        // 2. Reliable HTTP POST (debounced to 15s if WS is active, or immediate if WS disconnected)
        const now = Date.now();
        if (!isWsConnected || now - lastHttpSaveRef.current >= 15000) {
            lastHttpSaveRef.current = now;
            try {
                await axios.post(
                    `${config.api.baseUrl}/api/account/score/gta`,
                    { score: timeInSeconds },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } catch (error) {
                console.warn('[GTA Leaderboard] Save score failed:', error.message);
            }
        }
    }, [isAuthenticated, token, isWsConnected, sendMessage]);

    useEffect(() => {
        saveGtaScoreRef.current = saveGtaScore;
    }, [saveGtaScore]);

    // Fetch initial leaderboard when GTA is opened or expanded
    useEffect(() => {
        if (selectedGame === 'gta' && (isExpanded || isFullscreen)) {
            fetchGtaLeaderboard();
            const interval = setInterval(fetchGtaLeaderboard, 45000);
            return () => clearInterval(interval);
        }
    }, [selectedGame, isExpanded, isFullscreen, fetchGtaLeaderboard]);

    // Track play time continuously while GTA is running and active
    useEffect(() => {
        if (!isActive || selectedGame !== 'gta' || (!isExpanded && !isFullscreen) || !gtaLoaded) {
            return;
        }

        const timer = setInterval(() => {
            setGtaPlayTime(prev => {
                const next = prev + 1;
                try {
                    localStorage.setItem('gta_play_time', String(next));
                } catch {}
                // Periodically save score to backend every 5 seconds for live multi-user sync
                if (next % 5 === 0 && saveGtaScoreRef.current) {
                    saveGtaScoreRef.current(next);
                }
                return next;
            });
        }, 1000);

        return () => {
            clearInterval(timer);
            // Save on pause / switch / unmount
            try {
                const cur = parseInt(localStorage.getItem('gta_play_time') || '0', 10) || 0;
                if (cur > 0 && isAuthenticated && token) {
                    axios.post(
                        `${config.api.baseUrl}/api/account/score/gta`,
                        { score: cur },
                        { headers: { Authorization: `Bearer ${token}` } }
                    ).catch(() => {});
                }
            } catch {}
        };
    }, [isActive, selectedGame, isExpanded, isFullscreen, gtaLoaded, isAuthenticated, token]);

    // Keep container alive in DOM once active so games maintain state and do not reset on module stop
    const [hasEverBeenActive, setHasEverBeenActive] = useState(false);

    useEffect(() => {
        if (isActive) {
            setHasEverBeenActive(true);
        }
    }, [isActive]);

    // Send pause / resume signals to GTA iframe when module stops or active tab changes
    useEffect(() => {
        if (gtaIframeRef.current && gtaIframeRef.current.contentWindow) {
            const targetWindow = gtaIframeRef.current.contentWindow;
            if (!isActive || selectedGame !== 'gta') {
                targetWindow.postMessage({ type: 'GTA_PAUSE' }, '*');
            } else if (isActive && selectedGame === 'gta') {
                targetWindow.postMessage({ type: 'GTA_RESUME' }, '*');
            }
        }
    }, [isActive, selectedGame]);

    const handleResetGtaWorld = useCallback((e) => {
        if (e) e.stopPropagation();
        if (window.confirm(isEn ? 'Recreate procedural GTA world? Current progress will be reset and a new world will be generated.' : 'Пересоздать процедурный мир GTA? Текущий прогресс будет сброшен и сгенерирован новый мир.')) {
            if (gtaIframeRef.current && gtaIframeRef.current.contentWindow) {
                gtaIframeRef.current.contentWindow.postMessage({ type: 'GTA_RESET_WORLD' }, '*');
            }
        }
    }, [isEn]);

    const handlePlayGta = useCallback((e) => {
        if (e) e.stopPropagation();
        setSelectedGame('gta');
        if (!initGta) setInitGta(true);
        setIsExpanded(true);
        if (!hasBeenOpened) setHasBeenOpened(true);
    }, [initGta, hasBeenOpened]);

    // Dynamic tips while GTA 3D engine is downloading
    useEffect(() => {
        if (!initGta || gtaLoaded) return;
        const interval = setInterval(() => {
            setGtaLoadStep(prev => (prev + 1) % 3);
        }, 2200);
        return () => clearInterval(interval);
    }, [initGta, gtaLoaded]);

    // When expanding or switching, mark the currently selected game for initialization
    useEffect(() => {
        if (isExpanded || isFullscreen) {
            if (selectedGame === 'doodle' && !initDoodle) setInitDoodle(true);
            if (selectedGame === 'terraria' && !initTerraria) setInitTerraria(true);
            if (selectedGame === 'gta' && !initGta) setInitGta(true);
        }
    }, [isExpanded, isFullscreen, selectedGame, initDoodle, initTerraria, initGta]);

    const handleExpand = useCallback(() => {
        const next = !isExpanded;
        setIsExpanded(next);
        if (next && !hasBeenOpened) setHasBeenOpened(true);
    }, [isExpanded, hasBeenOpened]);

    const handleSwitch = useCallback((e, game) => {
        e.stopPropagation();
        setSelectedGame(game);
        if (!isExpanded) {
            setIsExpanded(true);
            if (!hasBeenOpened) setHasBeenOpened(true);
        }
    }, [isExpanded, hasBeenOpened]);

    const handleFullscreen = useCallback((e) => {
        e.stopPropagation();
        if (!containerRef.current) return;

        if (!document.fullscreenElement) {
            if (!isExpanded) {
                setIsExpanded(true);
                if (!hasBeenOpened) setHasBeenOpened(true);
            }
            containerRef.current.requestFullscreen().catch(err => {
                console.warn('Fullscreen request failed:', err);
            });
        } else {
            document.exitFullscreen();
        }
    }, [isExpanded, hasBeenOpened]);

    // Listen for fullscreen change events (including Escape key exit)
    useEffect(() => {
        const onFsChange = () => {
            const isFull = !!document.fullscreenElement;
            setIsFullscreen(isFull);
            setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
        };
        document.addEventListener('fullscreenchange', onFsChange);
        return () => document.removeEventListener('fullscreenchange', onFsChange);
    }, []);

    // After expanding or switching games, dispatch resize so canvases recalculate dimensions
    useEffect(() => {
        if (!hasBeenOpened) return;
        if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);

        if (isExpanded) {
            resizeTimerRef.current = setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 400);
        }

        return () => {
            if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
        };
    }, [isExpanded, hasBeenOpened, selectedGame]);

    const accentPalette = {
        purple: { color: '#a855f7', glow: 'rgba(168, 85, 247, 0.35)' },
        cyan:   { color: '#06b6d4', glow: 'rgba(6, 182, 212, 0.35)' },
        blue:   { color: '#3b82f6', glow: 'rgba(59, 130, 246, 0.35)' },
        red:    { color: '#ef4444', glow: 'rgba(239, 68, 68, 0.35)' },
        green:  { color: '#1bd96a', glow: 'rgba(27, 217, 106, 0.35)' },
        white:  { color: '#ffffff', glow: 'rgba(255, 255, 255, 0.3)' }
    };
    const accent = accentPalette[accentColor] || accentPalette.purple;

    // Keep in DOM once initialized so game progress is preserved across module stop/start
    if (!hasEverBeenActive) return null;

    return (
        <div
            className={`game-container-wrapper relative ${className}`}
            style={{ display: isActive ? 'block' : 'none' }}
        >
            <div
                ref={containerRef}
                className={`game-panel-root ${isExpanded ? 'expanded' : ''} ${isFullscreen ? 'is-fullscreen' : ''}`}
            >
                {/* Header bar: tabs on the left, controls on the right */}
                <div className="game-panel-header" onClick={handleExpand}>
                    {/* Left: Segmented Capsule Switcher */}
                    <div className="game-panel-left">
                        <div className="game-tabs-capsule" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className={`game-tab-btn ${selectedGame === 'doodle' ? 'active' : ''}`}
                                onClick={(e) => handleSwitch(e, 'doodle')}
                                style={selectedGame === 'doodle' ? { background: accent.color, color: '#000000', boxShadow: `0 2px 10px ${accent.glow}` } : {}}
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                </svg>
                                <span>DOODLE</span>
                            </button>
                            <button
                                type="button"
                                className={`game-tab-btn ${selectedGame === 'terraria' ? 'active' : ''}`}
                                onClick={(e) => handleSwitch(e, 'terraria')}
                                style={selectedGame === 'terraria' ? { background: accent.color, color: '#000000', boxShadow: `0 2px 10px ${accent.glow}` } : {}}
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <rect x="3" y="3" width="18" height="18" rx="2" />
                                    <path d="M3 9h18M9 21V9" />
                                </svg>
                                <span>TERRARIUM</span>
                            </button>

                            {/* GTA Button with popover bubble directly below it */}
                            <div className="gta-tab-anchor">
                                <button
                                    type="button"
                                    className={`game-tab-btn ${selectedGame === 'gta' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        setShowGtaInfo(false);
                                        handleSwitch(e, 'gta');
                                    }}
                                    style={selectedGame === 'gta' ? { background: accent.color, color: '#000000', boxShadow: `0 2px 10px ${accent.glow}` } : {}}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9C2.1 11.1 2 11.5 2 12v4c0 .6.4 1 1 1h2" />
                                        <circle cx="7" cy="17" r="2" />
                                        <path d="M9 17h6" />
                                        <circle cx="17" cy="17" r="2" />
                                    </svg>
                                    <span>GTA</span>
                                </button>

                                {/* Large rectangular image bubble directly under GTA button */}
                                {showGtaInfo && !isFullscreen && (
                                    <div
                                        className="gta-bubble-popover"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePlayGta(e);
                                        }}
                                        title={isEn ? "Open GTA" : "Открыть игру GTA"}
                                    >
                                        <div className="gta-bubble-caret" />
                                        <div className="gta-bubble-card">
                                            <img
                                                src="/games/gta/loading_bg.webp"
                                                alt="GTA"
                                                className="gta-bubble-image"
                                            />
                                            <button
                                                type="button"
                                                className="gta-bubble-close-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDismissGtaInfo(e);
                                                }}
                                                title={isEn ? "Close" : "Закрыть"}
                                            >
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                {/* Right: Controls & toggle button */}
                <div className="game-panel-right">
                    {/* Recreate GTA world button */}
                    {selectedGame === 'gta' && (isExpanded || isFullscreen) && (
                        <button
                            type="button"
                            className="game-action-btn"
                            onClick={handleResetGtaWorld}
                            title={isEn ? 'Recreate GTA World (New Seed)' : 'Пересоздать мир GTA (новый seed)'}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
                            </svg>
                        </button>
                    )}

                    {/* Fullscreen toggle button */}
                    {(isExpanded || isFullscreen) && (
                        <button
                            type="button"
                            className="game-action-btn"
                            onClick={handleFullscreen}
                            title={isFullscreen ? (isEn ? 'Exit fullscreen' : 'Выйти из полноэкранного') : (isEn ? 'Fullscreen' : 'Во весь экран')}
                        >
                            {isFullscreen ? (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                                </svg>
                            ) : (
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                                </svg>
                            )}
                        </button>
                    )}

                    {/* Toggle Expand/Collapse button matching .pov-toggle-btn */}
                    <button
                        type="button"
                        className="game-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleExpand();
                        }}
                    >
                        <span>{isExpanded ? (isEn ? 'Collapse' : 'Свернуть') : (isEn ? 'Expand' : 'Развернуть')}</span>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Subtle top neon border accent when open */}
            {(isExpanded || isFullscreen) && (
                <div
                    style={{
                        height: '2px',
                        background: `linear-gradient(90deg, transparent 0%, ${accent.color} 50%, transparent 100%)`,
                        boxShadow: `0 0 10px ${accent.glow}`,
                        flexShrink: 0
                    }}
                />
            )}

            {/* Body container matching .bot-pov-body & .pov-viewport-container */}
            {hasBeenOpened && (
                <div className="game-panel-body">
                    <div
                        className="game-viewport-frame"
                        style={{
                            height: isFullscreen ? '100%' : isExpanded ? (selectedGame === 'terraria' ? terrariaHeight : selectedGame === 'gta' ? gtaHeight : doodleHeight) : '0px',
                            opacity: isExpanded ? 1 : 0
                        }}
                    >
                        {/* Doodle Jump — once initialized, stays alive forever */}
                        <div style={{
                            display: selectedGame === 'doodle' ? 'block' : 'none',
                            width: '100%',
                            height: isFullscreen ? '100%' : doodleHeight
                        }}>
                            <DoodleJumpGame
                                isActive={initDoodle}
                                accentColor={accentColor}
                                onClose={onClose}
                            />
                        </div>

                        {/* Terrarium — once initialized, stays alive forever */}
                        <div style={{
                            display: selectedGame === 'terraria' ? 'block' : 'none',
                            width: '100%',
                            height: isFullscreen ? '100%' : terrariaHeight
                        }}>
                            <Terraria2Game
                                isActive={initTerraria}
                                isFullscreen={isFullscreen}
                                onClose={onClose}
                            />
                        </div>

                        {/* GTA 3D Web — once initialized, stays alive */}
                        <div style={{
                            display: selectedGame === 'gta' ? 'block' : 'none',
                            width: '100%',
                            height: isFullscreen ? '100%' : gtaHeight,
                            position: 'relative',
                            background: '#080c11',
                            borderRadius: '12px',
                            overflow: 'hidden'
                        }}>
                            {/* Loading overlay with animated spinner, progress bar and dynamic tips */}
                            {!gtaLoaded && (
                                <div style={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 10,
                                    gap: '14px',
                                    padding: '24px',
                                    overflow: 'hidden'
                                }}>
                                    {/* Darkened background image layer without blur */}
                                    <div style={{
                                        position: 'absolute',
                                        inset: 0,
                                        backgroundImage: "url('/games/gta/loading_bg.webp')",
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        filter: 'brightness(0.55)',
                                        zIndex: 0
                                    }} />

                                    {/* Cinematic vignette overlay */}
                                    <div style={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'radial-gradient(circle at center, rgba(15, 23, 42, 0.2) 0%, rgba(8, 12, 17, 0.72) 100%)',
                                        zIndex: 1
                                    }} />

                                    {/* Glowing Car Icon */}
                                    <div style={{
                                        position: 'relative',
                                        zIndex: 2,
                                        width: '54px',
                                        height: '54px',
                                        borderRadius: '16px',
                                        background: 'rgba(255, 255, 255, 0.06)',
                                        backdropFilter: 'blur(8px)',
                                        WebkitBackdropFilter: 'blur(8px)',
                                        border: '1px solid rgba(255, 255, 255, 0.15)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: `0 0 30px ${accent.glow}`,
                                        color: accent.color,
                                        animation: 'gtaPulse 1.8s infinite ease-in-out'
                                    }}>
                                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9C2.1 11.1 2 11.5 2 12v4c0 .6.4 1 1 1h2" />
                                            <circle cx="7" cy="17" r="2" />
                                            <path d="M9 17h6" />
                                            <circle cx="17" cy="17" r="2" />
                                        </svg>
                                    </div>

                                    {/* Status text with dynamic step */}
                                    <div style={{ textAlign: 'center' }}>
                                        <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.92rem', letterSpacing: '0.02em', marginBottom: '4px' }}>
                                            {gtaLoadStep === 0 && (isEn ? 'Connecting to GTA 3D engine…' : 'Подключение к 3D-миру GTA…')}
                                            {gtaLoadStep === 1 && (isEn ? 'Loading Three.js & Rapier3D physics…' : 'Загрузка Three.js и физики Rapier3D…')}
                                            {gtaLoadStep === 2 && (isEn ? 'Syncing procedural city shaders…' : 'Синхронизация процедурных шейдеров…')}
                                        </div>
                                        <div style={{ color: '#64748b', fontSize: '0.74rem', fontFamily: 'monospace' }}>
                                            {isEn ? 'High performance WebAssembly simulation' : 'Высокопроизводительная WebAssembly-симуляция'}
                                        </div>
                                    </div>

                                    {/* Animated loader bar */}
                                    <div style={{
                                        width: '220px',
                                        height: '4px',
                                        background: 'rgba(255, 255, 255, 0.08)',
                                        borderRadius: '2px',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}>
                                        <div style={{
                                            position: 'absolute',
                                            height: '100%',
                                            width: '50%',
                                            background: `linear-gradient(90deg, transparent, ${accent.color}, transparent)`,
                                            animation: 'gtaBarMove 1.3s infinite ease-in-out'
                                        }} />
                                    </div>
                                </div>
                            )}

                            {initGta && (
                                <iframe
                                    ref={gtaIframeRef}
                                    src="/games/gta/index.html"
                                    title="GTA Web"
                                    className="w-full h-full border-0"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none',
                                        borderRadius: '12px',
                                        background: '#080c11',
                                        display: 'block',
                                        opacity: gtaLoaded ? 1 : 0,
                                        transition: 'opacity 0.4s ease'
                                    }}
                                    onLoad={() => {
                                        setTimeout(() => {
                                            setGtaLoaded(true);
                                            if (gtaIframeRef.current && gtaIframeRef.current.contentWindow) {
                                                if (!isActive || selectedGame !== 'gta') {
                                                    gtaIframeRef.current.contentWindow.postMessage({ type: 'GTA_PAUSE' }, '*');
                                                }
                                            }
                                        }, 400);
                                    }}
                                    allow="pointer-lock; autoplay; fullscreen"
                                />
                            )}

                            {/* GTA Side Leaderboard Overlay - only shown if backend is connected */}
                            {initGta && gtaLoaded && (isWsConnected || backendConnected) && (
                                <div className="gta-side-leaderboard">
                                    <div className="gta-side-leaderboard-box">
                                        <div className="gta-side-leaderboard-header">
                                            <span className="gta-side-leaderboard-title">
                                                🏆 {isEn ? 'TOP TIME' : 'РЕЙТИНГ'}
                                            </span>
                                            <span style={{
                                                fontSize: '9px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                color: isWsConnected ? '#4ade80' : '#64748b'
                                            }} title={isWsConnected ? (isEn ? 'Real-time WebSocket Synchronized' : 'Синхронизировано в реальном времени') : (isEn ? 'Connecting...' : 'Подключение...')}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '6px',
                                                    height: '6px',
                                                    borderRadius: '50%',
                                                    backgroundColor: isWsConnected ? '#22c55e' : '#64748b',
                                                    boxShadow: isWsConnected ? '0 0 6px #22c55e' : 'none'
                                                }} />
                                                <span style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '0.5px' }}>
                                                    {isWsConnected ? 'LIVE' : 'SYNC'}
                                                </span>
                                            </span>
                                        </div>

                                        <div className="gta-side-leaderboard-list">
                                            {isLoadingGtaTop && gtaLeaderboard.length === 0 ? (
                                                <div style={{ fontSize: '9px', color: '#64748b', fontStyle: 'italic', padding: '2px 0' }}>
                                                    {isEn ? 'Loading…' : 'Загрузка…'}
                                                </div>
                                            ) : gtaLeaderboard.length > 0 ? (
                                                gtaLeaderboard.slice(0, 5).map((entry, idx) => {
                                                    const isCurrentUser = Boolean(user && (
                                                        (user.name && entry.name && user.name.toLowerCase() === entry.name.toLowerCase()) ||
                                                        (user.email && entry.name && (user.email.toLowerCase() === entry.name.toLowerCase() || user.email.split('@')[0].toLowerCase() === entry.name.toLowerCase()))
                                                    ));
                                                    const displayScore = isCurrentUser ? Math.max(entry.score, gtaPlayTime) : entry.score;
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className={`gta-side-leaderboard-item ${isCurrentUser ? 'is-current-user' : ''}`}
                                                            style={isCurrentUser ? {
                                                                background: 'rgba(56, 189, 248, 0.12)',
                                                                borderRadius: '4px',
                                                                padding: '1px 3px',
                                                                border: '1px solid rgba(56, 189, 248, 0.25)'
                                                            } : undefined}
                                                        >
                                                            <div className="gta-side-leaderboard-player">
                                                                <span className={`gta-side-leaderboard-rank ${idx === 0 ? 'rank-1' : idx === 1 ? 'rank-2' : idx === 2 ? 'rank-3' : 'rank-other'}`}>
                                                                    {idx + 1}.
                                                                </span>
                                                                <span className="gta-side-leaderboard-name" title={entry.name}>
                                                                    {entry.name || (isEn ? 'Player' : 'Игрок')}
                                                                    {isCurrentUser && (
                                                                        <span style={{ color: '#38bdf8', opacity: 0.85, fontSize: '8px', marginLeft: '3px' }}>★</span>
                                                                    )}
                                                                </span>
                                                            </div>
                                                            <span
                                                                className="gta-side-leaderboard-time"
                                                                style={isCurrentUser ? { color: accent.color || '#38bdf8', fontWeight: 700 } : undefined}
                                                            >
                                                                {formatPlayTime(displayScore)}
                                                            </span>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div style={{ fontSize: '9px', color: '#64748b', fontStyle: 'italic', padding: '2px 0' }}>
                                                    {isEn ? 'No records yet' : 'Пока нет лидеров'}
                                                </div>
                                            )}
                                        </div>

                                        {/* Current Player Live Play Time */}
                                        {isAuthenticated ? (
                                            <div className="gta-side-leaderboard-you">
                                                <span style={{ color: '#94a3b8', fontWeight: 600 }}>
                                                    {isEn ? 'YOU:' : 'ВЫ:'}
                                                </span>
                                                <span style={{
                                                    fontFamily: 'monospace',
                                                    color: accent.color || '#38bdf8',
                                                    fontWeight: 700
                                                }}>
                                                    {formatPlayTime(gtaPlayTime)}
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="gta-side-leaderboard-you" style={{ opacity: 0.85 }}>
                                                <span style={{ color: '#64748b', fontSize: '9px' }}>
                                                    {isEn ? 'YOU (Guest):' : 'ВЫ (Гость):'}
                                                </span>
                                                <span style={{
                                                    fontFamily: 'monospace',
                                                    color: '#94a3b8',
                                                    fontWeight: 600
                                                }}>
                                                    {formatPlayTime(gtaPlayTime)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
);
};

export default GameContainer;
